import type { InvoiceSummary } from "@/types/api";

export interface MissingMappingGroup {
  /** Source line text as it appears in the УПД. */
  source_name: string;
  /** Display label for the seller side — picker uses this for the second step. */
  kontragent_label: string;
  /** Seller ИНН from the invoice's creator (composer) or СвПрод. Used as group key. */
  kontragent_inn: string;
  /** One invoice ID that belongs to this group — used to fetch kontragent_ref_key via preview. */
  sample_invoice_id: string;
  /** How many files wait on this single mapping. */
  file_count: number;
  /** Whether this is a completely missing mapping vs a legacy pre-6.2 one. */
  kind: "missing" | "incomplete";
}

/** Group invoices that share the same unresolved nomenclature + seller.
 *
 *  One fix to the underlying NomenclatureMapping unblocks every file in the
 *  group, so the UI surfaces these groups as a "do N things to unblock M
 *  files" cheat sheet on top of the invoices list.
 */
export function aggregateMissingMappings(
  invoices: InvoiceSummary[],
): MissingMappingGroup[] {
  const groups = new Map<string, MissingMappingGroup>();

  for (const inv of invoices) {
    // Prefer composer (creator) ИНН — for commissioner schemes (Ozon) the
    // "kontragent" in 1С docs is the composer, not СвПрод.
    const inn = inv.creator_inn ?? inv.seller_inn ?? "";
    const label = inv.creator_name ?? inv.seller_name ?? "—";

    for (const name of inv.missing_nomenclature_names) {
      const key = `${inn}||${name}`;
      const existing = groups.get(key);
      if (existing) {
        existing.file_count += 1;
      } else {
        groups.set(key, {
          source_name: name,
          kontragent_label: label,
          kontragent_inn: inn,
          sample_invoice_id: inv.id,
          file_count: 1,
          kind: "missing",
        });
      }
    }

    for (const name of inv.incomplete_nomenclature_names) {
      const key = `${inn}||${name}`;
      const existing = groups.get(key);
      if (existing) {
        // Still count incomplete as a blocker (user must re-pick dogovor).
        existing.file_count += 1;
      } else {
        groups.set(key, {
          source_name: name,
          kontragent_label: label,
          kontragent_inn: inn,
          sample_invoice_id: inv.id,
          file_count: 1,
          kind: "incomplete",
        });
      }
    }
  }

  return Array.from(groups.values()).sort(
    (a, b) => b.file_count - a.file_count,
  );
}

export interface ReadinessCounts {
  total: number;
  parse_error: number;
  created: number;
  needs_nomenclature: number;
  incomplete_mapping: number;
  ready: number;
}

export function countReadiness(invoices: InvoiceSummary[]): ReadinessCounts {
  const counts: ReadinessCounts = {
    total: invoices.length,
    parse_error: 0,
    created: 0,
    needs_nomenclature: 0,
    incomplete_mapping: 0,
    ready: 0,
  };
  for (const inv of invoices) {
    counts[inv.readiness] += 1;
  }
  return counts;
}
